import React from "react";
import { Skeleton } from "antd";

/** KPI trend card — value, subtitle, trend lines + optional spark area */
export function DashAnalyticsTrendCardSkeleton({ showSpark = true }) {
  return (
    <div className="d-flex flex-wrap align-items-start justify-content-between gap-3 w-100 dash-card-skeleton">
      <div className="flex-grow-1" style={{ minWidth: 0 }}>
        <Skeleton active title={false} paragraph={{ rows: 3, width: ["55%", "38%", "48%"] }} />
      </div>
      {showSpark ? (
        <div
          className="dash-analytics-trend-card__spark flex-shrink-0 d-none d-lg-flex"
          style={{ width: "min(160px, 30%)", minHeight: 52 }}
        >
          <Skeleton.Node active className="w-100" style={{ width: "100%", height: 52 }}>
            <div style={{ width: "100%", height: 52, borderRadius: 6 }} />
          </Skeleton.Node>
        </div>
      ) : null}
    </div>
  );
}

/** Total revenue — chart bars + primary value + sub metrics */
export function DashTotalRevenueCardSkeleton() {
  const barHeights = [28, 44, 36, 52, 40, 48, 32, 56, 38, 46, 34, 50];
  return (
    <div className="dash-card-skeleton dash-card-skeleton--total-revenue h-100 d-flex flex-column">
      <div className="dash-total-revenue-card__chart-panel flex-shrink-0">
        <div className="dash-total-revenue-card__chart-head px-4 pt-4">
          <Skeleton.Input active size="small" style={{ width: 110, minHeight: 14 }} />
        </div>
        <div
          className="dash-total-revenue-card__chart-body px-4 pb-3 d-flex align-items-end justify-content-between gap-1"
          style={{ minHeight: 88 }}
        >
          {barHeights.map((h, i) => (
            <Skeleton.Button
              key={i}
              active
              shape="square"
              style={{ width: 6, minWidth: 6, height: h, padding: 0, borderRadius: 2 }}
            />
          ))}
        </div>
      </div>
      <div className="dash-total-revenue-card__body flex-grow-1 p-4 pt-3">
        <Skeleton active title={{ width: "70%" }} paragraph={{ rows: 1, width: "45%" }} />
        <div className="d-flex justify-content-between gap-3 mt-5">
          {[1, 2, 3].map((k) => (
            <div key={k} className="flex-grow-1 text-center">
              <Skeleton.Input active block style={{ maxWidth: 72, margin: "0 auto 6px" }} />
              <Skeleton.Input active size="small" style={{ maxWidth: 48, margin: "0 auto" }} />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/** Revenue strip metric card */
export function DashRevenueMetricCardSkeleton() {
  return (
    <div className="dash-card-skeleton dash-card-skeleton--metric h-100">
      <Skeleton active title={false} paragraph={{ rows: 3, width: ["50%", "65%", "42%"] }} />
    </div>
  );
}

/** P&L sidebar */
export function DashProfitLossSidebarSkeleton() {
  return (
    <div className="dash-card-skeleton dash-card-skeleton--pl p-5">
      <Skeleton active title={{ width: "55%" }} paragraph={{ rows: 1, width: "75%" }} className="mb-5" />
      <Skeleton active title={false} paragraph={{ rows: 6, width: ["100%", "92%", "88%", "95%", "90%", "85%"] }} />
      <div className="mt-5 pt-4 border-top border-gray-200">
        <Skeleton active title={{ width: "40%" }} paragraph={{ rows: 2, width: ["60%", "50%"] }} />
      </div>
    </div>
  );
}

/** Cost breakdown list */
export function DashCostBreakdownSkeleton() {
  return (
    <div className="dash-card-skeleton dash-card-skeleton--cost d-flex flex-column flex-grow-1">
      {[0, 1, 2, 3, 4].map((i) => (
        <div key={i} className={i > 0 ? "pt-5 mt-5 border-top border-gray-200 border-top-dashed" : ""}>
          <div className="d-flex justify-content-between gap-3 mb-2">
            <Skeleton.Input active size="small" style={{ width: "45%", minHeight: 14 }} />
            <Skeleton.Input active size="small" style={{ width: 72, minHeight: 14 }} />
          </div>
          <Skeleton.Button active block style={{ height: 8, minWidth: "100%", borderRadius: 999 }} />
        </div>
      ))}
      <div className="mt-5 pt-4">
        <div className="d-flex justify-content-between">
          <Skeleton.Input active size="small" style={{ width: 100 }} />
          <Skeleton.Input active style={{ width: 120 }} />
        </div>
      </div>
    </div>
  );
}

export function DashUnitsNetProfitChartCanvasSkeleton() {
  return (
    <div className="dash-card-skeleton dash-card-skeleton--chart-canvas h-100 d-flex align-items-stretch">
      <Skeleton.Node active style={{ width: "100%", height: "100%", minHeight: 180 }}>
        <div style={{ width: "100%", height: "100%", minHeight: 180, borderRadius: 8 }} />
      </Skeleton.Node>
    </div>
  );
}

export function DashUnitsNetProfitChartFooterSkeleton() {
  return (
    <div className="dash-card-skeleton dash-card-skeleton--chart-footer row g-3 text-center text-md-start">
      {[1, 2, 3, 4].map((k) => (
        <div key={k} className="col-3 col-md-3">
          <Skeleton.Input active size="small" block style={{ marginBottom: 6, maxWidth: 72 }} />
          <Skeleton.Input active style={{ maxWidth: 80, margin: "0 auto" }} />
        </div>
      ))}
    </div>
  );
}

/** Add-metric picker button */
export function DashMetricsPickerSkeleton() {
  return (
    <Skeleton.Button active shape="circle" style={{ width: 44, height: 44, minWidth: 44 }} />
  );
}
